import { Box, Heading, Text, Spinner } from "@chakra-ui/react";
import Select from "react-select";
import styled, { useTheme } from "styled-components";
import { MapPin, Home, Hash } from "lucide-react";
import { useSchedule } from "../context/ScheduleProvider";
import { AppTheme } from "../theme";
import { THEME } from "../helpers/constans";

// Custom select styles for modern look
const getSelectStyles = (theme: AppTheme) => ({
  control: (base: any, state: any) => ({
    ...base,
    background: theme.background2,
    borderColor: state.isFocused ? theme.primary : theme.border,
    borderWidth: "2px",
    borderRadius: THEME.borderRadius,
    padding: "6px 10px",
    boxShadow: state.isFocused ? `0 0 0 3px ${theme.primary}20` : theme.shadowSm,
    transition: "all 0.2s ease",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: "16px",
    minHeight: "44px",
    "&:hover": {
      borderColor: theme.primary,
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected 
      ? theme.primary 
      : state.isFocused 
        ? `${theme.primary}15`
        : theme.background2,
    color: state.isSelected ? "white" : theme.text,
    padding: "14px 16px",
    cursor: "pointer",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: "16px",
    fontWeight: state.isSelected ? 600 : 500,
    transition: "background 0.15s ease",
    minHeight: "44px",
    "&:active": {
      background: `${theme.primary}25`,
    },
  }),
  menu: (base: any) => ({
    ...base,
    background: theme.background2,
    border: `1px solid ${theme.border}`,
    borderRadius: THEME.borderRadius,
    boxShadow: theme.shadowLg,
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: theme.textMuted,
    fontWeight: 500,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: theme.text,
    fontWeight: 500,
  }),
  input: (base: any) => ({
    ...base,
    color: theme.text,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? theme.primary : theme.textMuted,
    transition: "all 0.2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0)",
    "&:hover": {
      color: theme.primary,
    },
  }),
});

const FormSection = styled.div<{ $theme: AppTheme }>`
  margin-bottom: 1.25rem;
`;

const LabelContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: ${({ $theme }) => $theme.text};
`;

const IconWrapper = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ $theme }) => `${$theme.primary}15`};
  color: ${({ $theme }) => $theme.primary};
`;

const LoadingContainer = styled.div<{ $theme: AppTheme }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
`;

export const SelectAddressForm = () => {
  const theme = useTheme() as AppTheme;
  const selectStyles = getSelectStyles(theme);
  
  const {
    cityId,
    formattedCities,
    formattedStreets,
    addresses,
    formattedNumbers,
    setAddressId,
    setAddressLabel,
    setCityId,
    setStreetId,
    setStreetName,
    setAddresses,
    isLoading,
  } = useSchedule();

  if (isLoading) {
    return (
      <LoadingContainer $theme={theme}>
        <Spinner 
          size="lg" 
          color={theme.primary}
          thickness="3px"
          speed="0.8s"
        />
        <Text 
          mt={4} 
          color={theme.textSecondary}
          fontWeight={500}
          fontFamily="'Plus Jakarta Sans', sans-serif"
        >
          Wczytywanie harmonogramu...
        </Text>
      </LoadingContainer>
    );
  }

  return (
    <Box>
      <Heading 
        color={theme.text} 
        size="md"
        fontFamily="'Outfit', sans-serif"
        fontWeight={700}
        mb={1}
      >
        Wybierz rejon wywozu
      </Heading>
      <Text 
        color={theme.textMuted} 
        fontSize="sm" 
        mb={6}
        fontWeight={500}
      >
        Wybierz swoją lokalizację, aby zobaczyć harmonogram
      </Text>

      <FormSection $theme={theme}>
        <LabelContainer $theme={theme}>
          <IconWrapper $theme={theme}>
            <MapPin size={14} />
          </IconWrapper>
          <Text fontSize="sm" fontWeight={600}>
            Miejscowość
          </Text>
        </LabelContainer>
        <Select
          placeholder="Wybierz miejscowość..."
          options={formattedCities}
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          onChange={(e: any) => {
            setCityId(e.value);
            setStreetId(null);
            setStreetName(null);
            setAddresses(null);
          }}
          noOptionsMessage={() => "Brak wyników"}
        />
      </FormSection>

      {cityId && (
        <FormSection $theme={theme}>
          <LabelContainer $theme={theme}>
            <IconWrapper $theme={theme}>
              <Home size={14} />
            </IconWrapper>
            <Text fontSize="sm" fontWeight={600}>
              Ulica
            </Text>
          </LabelContainer>
          <Select
            placeholder="Wybierz ulicę..."
            options={formattedStreets ?? []}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            onChange={(e: any) => {
              setStreetId(e.value);
              setStreetName(e.label);
              setAddresses(null);
            }}
            noOptionsMessage={() => "Brak wyników"}
          />
        </FormSection>
      )}

      {addresses && (
        <FormSection $theme={theme}>
          <LabelContainer $theme={theme}>
            <IconWrapper $theme={theme}>
              <Hash size={14} />
            </IconWrapper>
            <Text fontSize="sm" fontWeight={600}>
              Numer domu
            </Text>
          </LabelContainer>
          <Select
            placeholder="Wybierz numer..."
            options={formattedNumbers ?? []}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            onChange={(e: any) => {
              // Fix multiple street id for kleszczewo ex. 4567,5607
              const singleId = e.value.split(',')[0];
              setAddressId(singleId);
              setAddressLabel(e.label);
            }}
            noOptionsMessage={() => "Brak wyników"}
          />
        </FormSection>
      )}
    </Box>
  );
};
